import {ModelValidation, ModelSectionValidation, ModelFactory, DependencyRegistrer} from '@/engine';
import {SectionValidation, SupportsSwitch, Validation} from '@/model/types';
import {getValidator, getSectionValidator, resolveNumericBound} from '../validators';
import {getConditionDependencies} from './condition';
import {createFactoryFromSwitch} from './switch';

export const createValidationFactory = (
  validation: Validation,
  dependencyRegistrer?: DependencyRegistrer,
): ModelValidation<typeof validation> => {
  const validator = getValidator(validation);

  dependencyRegistrer?.('all', ...getValidationDependencies(validation));

  return (valueResolver, visibilitySet) => {
    const predicate = validator(valueResolver, visibilitySet);
    return (value?: string) => {
      if (predicate(value)) return {valid: true};
      const {type, ...extra} = validation;
      if (
        validation.type === 'min' ||
        validation.type === 'minEqual' ||
        validation.type === 'max' ||
        validation.type === 'maxEqual'
      ) {
        return {valid: false, error: type, extra: {...extra, value: resolveNumericBound(validation.value)}};
      }
      return {valid: false, error: type, extra};
    };
  };
};
export const processValidations = (
  validations: SupportsSwitch<Validation[]>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<ModelValidation<Validation>[]> => {
  if (Array.isArray(validations)) {
    return () => validations.map(validation => createValidationFactory(validation, dependencyRegistrer));
  }
  return createFactoryFromSwitch(validations, [], {
    process: validations => validations.map(validation => createValidationFactory(validation, dependencyRegistrer)),
  });
};

export const createSectionValidationFactory = (
  validation: SectionValidation,
  dependencyRegistrer?: DependencyRegistrer,
): ModelSectionValidation<typeof validation> => {
  const validator = getSectionValidator(validation);

  dependencyRegistrer?.('all', ...getSectionValidationDependencies(validation));

  return (valueResolver, visibilitySet) => {
    const predicate = validator(valueResolver, visibilitySet);
    return () => {
      if (predicate()) return {valid: true};
      const {type, ...extra} = validation;
      return {valid: false, error: type, extra};
    };
  };
};

export const processSectionValidations = (
  validations: SupportsSwitch<SectionValidation[]>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<ModelSectionValidation<SectionValidation>[]> => {
  if (Array.isArray(validations)) {
    return () => validations.map(validation => createSectionValidationFactory(validation, dependencyRegistrer));
  }
  return createFactoryFromSwitch(validations, [], {
    process: validations =>
      validations.map(validation => createSectionValidationFactory(validation, dependencyRegistrer)),
  });
};

export const getSectionValidationDependencies = (validation: SectionValidation): string[] => {
  switch (validation.type) {
    case 'sum':
      return validation.fields;
    case 'groupSum':
      return [validation.fields.$query, `${validation.fields.groupBy}*`];
    case 'groupDistinct':
      return [validation.fields.$query, `${validation.fields.groupBy}*`];
    case 'distinct':
      return validation.fields;
    case 'condition':
      return getConditionDependencies(validation.condition);
    default:
      return [];
  }
};

export const getValidationDependencies = (validation: Validation): string[] => {
  switch (validation.type) {
    case 'sameAs':
    case 'notIn':
      return [validation.value.$ref];
    case 'condition':
      return getConditionDependencies(validation.condition);
    case 'maxSum':
      return typeof validation.value === 'object' ? [...validation.fields, validation.value.$ref] : validation.fields;
    case 'length':
    case 'minLength':
    case 'maxLength': {
      if (typeof validation.value === 'object' && '$ref' in validation.value) {
        return [validation.value.$ref];
      }
      return [];
    }
    default:
      return [];
  }
};
